import { StyledTooltip } from "@/components/StyledTooltip/StyledTooltip";
import { Box, Slider, Typography } from "@mui/material";
import React, { useEffect, useId, useRef } from "react";

type LabelledSliderProps = {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  tooltip?: React.ReactNode;
  valueLabelFormat?: (value: number) => string;
  onChange: (event: Event, value: number | number[]) => void;
  onInteractionChange?: (isInteracting: boolean) => void;
};

export const LabelledSlider: React.FC<LabelledSliderProps> = ({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  tooltip,
  valueLabelFormat,
  onInteractionChange
}) => {
  const draggingRef = useRef(false);
  const hoveringRef = useRef(false);
  const labelId = useId();

  useEffect(() => {
    if (!onInteractionChange) {
      return;
    }
    const handleWindowPointerUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false;
        if (!hoveringRef.current) {
          onInteractionChange(false);
        }
      }
    };
    window.addEventListener("pointerup", handleWindowPointerUp);
    return () => window.removeEventListener("pointerup", handleWindowPointerUp);
  }, [onInteractionChange]);

  const handleThumbMouseEnter = () => {
    hoveringRef.current = true;
    onInteractionChange?.(true);
  };
  const handleThumbMouseLeave = () => {
    hoveringRef.current = false;
    if (!draggingRef.current) {
      onInteractionChange?.(false);
    }
  };
  const handleThumbPointerDown = () => {
    draggingRef.current = true;
    onInteractionChange?.(true);
  };

  return (
    <StyledTooltip placement="bottom" title={tooltip}>
      <Box sx={{ display: "flex", justifyContent: "flex-start", gap: "8px", height: "fit-content" }}>
        {/* Label */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            id={labelId}
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 400,
              fontSize: "18px",
              width: "180px",
              height: "22px"
            }}
          >
            {label}:
          </Typography>
        </Box>

        {/* Slider */}
        <Box sx={{ paddingLeft: "8px", width: "180px", flexShrink: 0, display: "flex", alignItems: "center" }}>
          <Slider
            sx={{ width: "180px" }}
            value={value}
            min={min}
            max={max}
            step={step}
            marks
            valueLabelDisplay="auto"
            valueLabelFormat={valueLabelFormat}
            aria-labelledby={labelId}
            getAriaValueText={valueLabelFormat}
            onChange={onChange}
            slotProps={{
              thumb: {
                onMouseEnter: handleThumbMouseEnter,
                onMouseLeave: handleThumbMouseLeave,
                onPointerDown: handleThumbPointerDown
              }
            }}
          />
        </Box>
      </Box>
    </StyledTooltip>
  );
};
